import Axios from 'axios';
import { SERVER_URL } from 'Constants';
import { IUserResult } from 'Models/Store';
import { buildUrl } from 'Util/FetchUtils';
import { stringNotEmpty } from 'Util/TypeGuards';

type Configure2faResponseBase = {
	method: string;
};
export type ConfigureAuthenticatorResponse = Configure2faResponseBase & {
	method: 'Authenticator';
	code: string;
};
export type ConfigureEmailResponse = Configure2faResponseBase & {
	method: 'Email';
};
export type Configure2faResponse = ConfigureAuthenticatorResponse | ConfigureEmailResponse;
export function configure2fa(userName: string, method: string): Promise<Configure2faResponse> {
	const url = `${SERVER_URL}/api/account/configure-2fa`;
	const body = { method: method, userName: userName };
	return Axios.post<Configure2faResponse>(url, body).then(x => x.data);
}
export function disable2fa(userName: string, reason?: string) {
	const url = `${SERVER_URL}/api/account/disable-2fa`;
	const body = { userName, reason };
	return Axios.post(url, body);
}
export function valid2faMethods(userName: string): Promise<string[]> {
	const url = buildUrl(`${SERVER_URL}/api/account/valid-2fa`, { userName });
	return Axios.get<string[]>(url).then(x => x.data);
}
export function me(): Promise<IUserResult> {
	return Axios.get<IUserResult>(`${SERVER_URL}/api/account/me`).then(x => x.data);
}
export function resetPasswordRequest(userName: string) {
	return Axios.post<void>(`${SERVER_URL}/api/account/reset-password-request`, {
		username: userName,
	});
}
export function resetPassword(token: string, userName: string, password: string, activateAccount = false) {
	return Axios.post(`${SERVER_URL}/api/account/reset-password`, {
		token,
		username: userName,
		password,
		activateAccount,
	});
}
export function toggleUserActivation(activated: boolean, userName: string, reason: string) {
	return Axios.post(`${SERVER_URL}/api/account/toggle-user-activation`, {
		Username: userName,
		activated,
		reason,
	});
}

export function deleteUserById(userId: string, reason?: string) {
	return Axios.delete(`${SERVER_URL}/api/account/users/${userId}`, {
		data: { reason: reason ?? '' },
	});
}

export function enable2fa(userName: string, reason?: string): Promise<Configure2faResponse> {
	const url = `${SERVER_URL}/api/account/enable-2fa`;
	const body = { username: userName, reason };
	return Axios.post<Configure2faResponse>(url, body).then(x => x.data);
}

export type CheckUserEmailResponse = {
	/** Only returned to staff. Anonymous callers learn that the account exists, not whose it is. */
	firstName: string | null;
	initials: string;
	resetRequired: boolean;
	/**
	 * Whether resetRequired is owed to the legacy data migration rather than to staff having created
	 * the account. Both have to set a password before they can log in; only migrated customers are
	 * told their details came across from an old system.
	 */
	isMigratedUser: boolean;
	/** Deactivated by staff. A failed-attempt lockout is reported by lockoutMinutes instead. */
	isDisabled: boolean;
	isGuest: boolean;
	/** Minutes left on a failed-attempt lockout; 0 when not locked out. */
	lockoutMinutes: number;
};

export async function checkUserEmail(userName: string): Promise<CheckUserEmailResponse | null> {
	try {
		const response = await Axios.post<CheckUserEmailResponse | null>(`${SERVER_URL}/api/account/check-user-email`, {
			Username: userName.toLowerCase(),
		});

		if (response.status !== 200) {
			return null;
		}

		return response.data;
	} catch {
		return null;
	}
}

export async function checkUserMobile(phoneNumber: string) {
	try {
		const response = await Axios.post(`${SERVER_URL}/api/account/check-user-mobile`, { Username: phoneNumber });

		if (response.status !== 200) {
			return null;
		}

		return response.data;
	} catch {
		return null;
	}
}

export function updatePassword(newPassword: string) {
	return Axios.post(`${SERVER_URL}/api/account/change-password`, {
		NewPassword: newPassword,
	});
}

export function updatePasswordByStaff({ newPassword, username }: { newPassword: string; username: string }) {
	return Axios.post(`${SERVER_URL}/api/account/change-password-by-staff`, { newPassword, username });
}

export function sendActivateAccountRequest(userName: string) {
	return Axios.post<void>(`${SERVER_URL}/api/account/activate-account-request`, {
		username: userName,
	});
}

export function changeUserGroups(
	userName: string,
	groups: string[],
	oldDisplayNameId?: string,
	newDisplayNameId?: string,
) {
	return Axios.post<void>(`${SERVER_URL}/api/account/change-user-groups`, {
		userName,
		groups,
		oldDisplayNameId,
		newDisplayNameId,
	});
}

export function changeEmailRequest(newEmail: string) {
	return Axios.post<void>(`${SERVER_URL}/api/account/change-email-request`, {
		newEmail,
	});
}

export function changeEmailRequestByStaff(username: string, newEmail: string) {
	return Axios.post<void>(`${SERVER_URL}/api/account/change-email-request-by-staff`, {
		username,
		newEmail,
	});
}
export function cancelChangeEmailRequest(userId: string) {
	return Axios.post<void>(`${SERVER_URL}/api/account/cancel-change-email-request`, {
		userId: userId,
	});
}

/**
 * Returns true if email is associated to an account. False otherwise.
 */
export async function doesUserExistViaEmail(email: string) {
	const result = await checkUserEmail(email);
	return result !== null;
}

/**
 * Returns true if mobile is associated to an account. False otherwise.
 */
export async function doesUserExistViaMobile(mobile: string) {
	const result = await checkUserMobile(mobile);
	return result !== null;
}

/**
 * Captures the cart-page "Subscribe to marketing communications" choice as an acknowledgment
 * for the booking owner. Called once the user proceeds past the cart step — completing that
 * step is what counts as acknowledgment.
 */
export async function acknowledgeMarketingPreference(userId: string, subscribed: boolean) {
	const url = `${SERVER_URL}/api/account/marketing-acknowledgment`;
	return Axios.post(url, { userId, subscribed });
}
